import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import * as AccountsDB from '@/lib/personalAccountsDb';

function getStatusStyle(status: string) {
  const map: Record<string, string> = {
    'active': 'bg-emerald-100 text-emerald-800',
    'closed': 'bg-foreground-100 text-foreground-500',
    'archived': 'bg-foreground-100 text-foreground-400',
  };
  return map[status] || 'bg-foreground-100 text-foreground-500';
}

function getStatusLabel(status: string) {
  const map: Record<string, string> = {
    'active': 'Aktif',
    'closed': 'Kapalı',
    'archived': 'Arşiv',
  };
  return map[status] || status;
}

interface AccountWithBalance extends AccountsDB.PersonalAccount {
  total_given: number;
  total_received: number;
  remaining: number;
  last_movement_date: string | null;
}

export default function PersonalAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountsDB.PersonalAccount[]>([]);
  const [globalBalance, setGlobalBalance] = useState<AccountsDB.GlobalBalance | null>(null);
  const [monthlySummary, setMonthlySummary] = useState<AccountsDB.MonthlySummary | null>(null);
  const [accountBalances, setAccountBalances] = useState<Record<string, { total_given: number; total_received: number; remaining: number; last_movement_date: string | null }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountsRes, balanceRes, monthlyRes] = await Promise.all([
        AccountsDB.getPersonalAccounts(statusFilter === 'archived'),
        AccountsDB.getAccountBalance(),
        AccountsDB.getMonthlySummary(),
      ]);

      if (accountsRes.error) {
        setError(accountsRes.error);
        return;
      }

      const accts = accountsRes.data || [];
      setAccounts(accts);
      setGlobalBalance(balanceRes.data as AccountsDB.GlobalBalance);
      setMonthlySummary(monthlyRes.data);

      const balances: Record<string, any> = {};
      for (const acct of accts) {
        const balRes = await AccountsDB.getAccountBalance(acct.id);
        if (balRes.data) {
          balances[acct.id] = balRes.data;
        }
      }
      setAccountBalances(balances);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const accountsWithBalance = useMemo((): AccountWithBalance[] => {
    return accounts.map(a => ({
      ...a,
      total_given: accountBalances[a.id]?.total_given || 0,
      total_received: accountBalances[a.id]?.total_received || 0,
      remaining: accountBalances[a.id]?.remaining || 0,
      last_movement_date: accountBalances[a.id]?.last_movement_date || null,
    }));
  }, [accounts, accountBalances]);

  const filteredAccounts = useMemo(() => {
    let result = [...accountsWithBalance];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.account_name.toLowerCase().includes(q) ||
        a.account_no.toLowerCase().includes(q) ||
        (a.phone && a.phone.includes(q))
      );
    }

    if (statusFilter !== 'all' && statusFilter !== 'archived') {
      result = result.filter(a => a.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(a => a.person_type === typeFilter);
    }

    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => a.opened_at.localeCompare(b.opened_at));
        break;
      case 'name':
        result.sort((a, b) => a.account_name.localeCompare(b.account_name, 'tr'));
        break;
      case 'balance_desc':
        result.sort((a, b) => b.remaining - a.remaining);
        break;
      default:
        break;
    }

    return result;
  }, [accountsWithBalance, search, statusFilter, typeFilter, sortBy]);

  const remainingValue = globalBalance?.total_remaining ?? 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Kişisel Hesaplar</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Şahsi hesap defteri — satış, müşteri ve senetlerden bağımsızdır</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="btn-primary whitespace-nowrap flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          <span>Yeni Hesap Aç</span>
        </button>
      </div>

      {/* Summary Cards — 4 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Aktif Hesap</p>
          <p className="text-2xl font-bold text-foreground-900">{globalBalance?.active_count ?? 0}</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Kişisel Alacak</p>
          <p className={`text-2xl font-bold ${remainingValue >= 0 ? 'text-amber-600' : 'text-red-500'}`}>
            {formatTRY(Math.abs(remainingValue))}
          </p>
          <p className="text-[10px] text-foreground-400 mt-0.5">{remainingValue >= 0 ? 'şahıslardan alacak' : 'şahıslara borç'}</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Bu Ay Verilen</p>
          <p className="text-2xl font-bold text-amber-600">
            {formatTRY(monthlySummary?.month_given ?? 0)}
          </p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Bu Ay Tahsil Edilen</p>
          <p className="text-2xl font-bold text-emerald-600">
            {formatTRY(monthlySummary?.month_received ?? 0)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Kişi adı, hesap no veya telefon ile ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="active">Aktif</option>
          <option value="closed">Kapalı</option>
          <option value="archived">Arşivlenen</option>
          <option value="all">Tümü</option>
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="all">Tüm Kişi Türleri</option>
          {Object.entries(AccountsDB.PERSON_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="newest">En Yeni</option>
          <option value="oldest">En Eski</option>
          <option value="name">İsme Göre</option>
          <option value="balance_desc">Bakiyeye Göre</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-400 text-lg"></i>
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadData} className="ml-auto btn-primary text-xs cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {/* Accounts Table - Desktop */}
      <div className="hidden lg:block stat-card overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-background-200">
              <th className="table-header">Hesap No</th>
              <th className="table-header">Ad Soyad</th>
              <th className="table-header">Telefon</th>
              <th className="table-header">Toplam Borç</th>
              <th className="table-header">Toplam Tahsilat</th>
              <th className="table-header">Kalan Bakiye</th>
              <th className="table-header">Son Hareket</th>
              <th className="table-header">Durum</th>
              <th className="table-header"></th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map(account => (
              <tr
                key={account.id}
                className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/personal-accounts/${account.id}`)}
              >
                <td className="table-cell font-medium text-primary-600 whitespace-nowrap">{account.account_no}</td>
                <td className="table-cell font-medium">{account.account_name}</td>
                <td className="table-cell text-foreground-500 text-xs">
                  {account.phone ? (
                    <div className="flex items-center gap-1.5">
                      <span>{account.phone}</span>
                      <a
                        href={`tel:${account.phone}`}
                        onClick={e => e.stopPropagation()}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 hover:bg-emerald-100 cursor-pointer flex-shrink-0"
                        title="Ara"
                      >
                        <i className="ri-phone-line text-xs"></i>
                      </a>
                    </div>
                  ) : '—'}
                </td>
                <td className="table-cell text-sm font-medium text-amber-600">{formatTRY(account.total_given)}</td>
                <td className="table-cell text-sm font-medium text-emerald-600">{formatTRY(account.total_received)}</td>
                <td className="table-cell text-sm font-semibold">
                  <span className={account.remaining > 0 ? 'text-amber-600' : account.remaining < 0 ? 'text-red-500' : 'text-foreground-500'}>
                    {formatTRY(Math.abs(account.remaining))}
                    {account.remaining !== 0 && (
                      <span className="text-xs font-normal ml-1">{account.remaining > 0 ? 'Alacak' : 'Borç'}</span>
                    )}
                  </span>
                </td>
                <td className="table-cell text-xs text-foreground-500">
                  {account.last_movement_date
                    ? new Date(account.last_movement_date).toLocaleDateString('tr-TR')
                    : '—'}
                </td>
                <td className="table-cell py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getStatusStyle(account.status)}`}>
                    {getStatusLabel(account.status)}
                  </span>
                </td>
                <td className="table-cell py-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/personal-accounts/${account.id}`); }}
                    className="text-xs text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line"></i> Detay
                  </button>
                </td>
              </tr>
            ))}
            {filteredAccounts.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm text-foreground-400">
                  {search || typeFilter !== 'all'
                    ? 'Filtrelere uygun kayıt bulunamadı.'
                    : 'Henüz kişisel hesap kaydı yok. "Yeni Hesap Aç" butonuna tıklayarak ilk kaydı oluşturabilirsiniz.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Accounts Cards - Mobile */}
      <div className="lg:hidden space-y-3">
        {filteredAccounts.map(account => (
          <div
            key={account.id}
            onClick={() => navigate(`/personal-accounts/${account.id}`)}
            className="stat-card p-4 cursor-pointer space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-primary-600">{account.account_no}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyle(account.status)}`}>
                {getStatusLabel(account.status)}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800">{account.account_name}</p>
              <p className="text-xs text-foreground-400">
                {account.phone ? (
                  <span className="flex items-center gap-1.5">
                    {account.phone}
                    <a
                      href={`tel:${account.phone}`}
                      onClick={e => e.stopPropagation()}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 hover:bg-emerald-100 cursor-pointer"
                      title="Ara"
                    >
                      <i className="ri-phone-line text-[10px]"></i>
                    </a>
                  </span>
                ) : 'Telefon yok'}
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-amber-600 font-medium">Borç: {formatTRY(account.total_given)}</span>
              <span className="text-emerald-600 font-medium">Tahsilat: {formatTRY(account.total_received)}</span>
              <span className={`font-semibold ${account.remaining > 0 ? 'text-amber-600' : account.remaining < 0 ? 'text-red-500' : 'text-foreground-500'}`}>
                Kalan: {formatTRY(Math.abs(account.remaining))}
              </span>
            </div>
          </div>
        ))}
        {filteredAccounts.length === 0 && (
          <p className="text-center py-8 text-sm text-foreground-400">
            {search || typeFilter !== 'all'
              ? 'Filtrelere uygun kayıt bulunamadı.'
              : 'Henüz kişisel hesap kaydı yok.'}
          </p>
        )}
      </div>

      {/* New Account Modal */}
      {showNewModal && (
        <NewAccountModal
          onClose={() => setShowNewModal(false)}
          onSuccess={() => { setShowNewModal(false); loadData(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// New Account Modal Component
// ============================================================
function NewAccountModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    account_name: '',
    person_type: 'other',
    phone: '',
    secondary_phone: '',
    address: '',
    description: '',
    notes: '',
    opened_at: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.account_name.trim()) {
      setErr('Ad soyad zorunludur.');
      return;
    }
    if (!form.phone.trim()) {
      setErr('Telefon zorunludur.');
      return;
    }
    setSaving(true);
    setErr(null);
    const res = await AccountsDB.createPersonalAccount({
      account_name: form.account_name.trim(),
      person_type: form.person_type,
      phone: form.phone.trim(),
      secondary_phone: form.secondary_phone.trim() || null,
      address: form.address.trim() || null,
      description: form.description.trim() || null,
      notes: form.notes.trim() || null,
      opened_at: form.opened_at,
    });
    setSaving(false);
    if (res.error) {
      setErr(res.error);
      return;
    }
    onSuccess();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-background-50 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h3 className="text-lg font-semibold text-foreground-900">Yeni Hesap Defteri Aç</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ad Soyad *</label>
            <input
              type="text"
              value={form.account_name}
              onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="Kişi adı ve soyadı"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Telefon *</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="05XX XXX XX XX"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">İkinci Telefon</label>
              <input
                type="text"
                value={form.secondary_phone}
                onChange={e => setForm(f => ({ ...f, secondary_phone: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="05XX XXX XX XX"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kişi Türü</label>
            <select
              value={form.person_type}
              onChange={e => setForm(f => ({ ...f, person_type: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
            >
              {Object.entries(AccountsDB.PERSON_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Açılış Tarihi</label>
            <input
              type="date"
              value={form.opened_at}
              onChange={e => setForm(f => ({ ...f, opened_at: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Adres</label>
            <textarea
              value={form.address}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Adres bilgisi"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Açıklama</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="Kısa açıklama"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Not</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Özel notlar"
            />
          </div>

          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer whitespace-nowrap">
              İptal
            </button>
            <button type="submit" disabled={saving} className="flex-1 btn-primary whitespace-nowrap cursor-pointer">
              {saving ? 'Kaydediliyor...' : 'Defter Aç'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}